import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import { AllowBlockListType, PhishingAction } from "@/constants/phishing";
import { downloadFile } from "@/_helpers/download";
import type { Paged, Pagination } from "@/types";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import {
  type AllowBlockListForm,
  type AllowBlockListItem,
  type SortItem,
  type ValidateAllowBlockTermsPayload,
  type ValidateAllowBlockTermsResponse,
  type TldTermsResponse,
} from "@/_store/email-security/email-settings.module.ts";
import { StoreModule } from "@/constants/store.ts";

export interface MspEmailSecurityAllowBlockListState {
  allowBlockList: Paged<AllowBlockListItem>;
  pagination: Pagination;
  loading: boolean;
  /** Sorting options string in format "field,order" */
  sortingOptions?: string;
}

export const defaultMspEmailSecurityAllowBlockListState = {
  allowBlockList: {
    items: [],
    total: 0,
  },
  pagination: {
    page: 0,
    pageSize: 25,
  },
  sortingOptions: undefined,
  loading: false,
};

/**
 * Store for managing MSP Email Security Allow/Block Lists
 */
export const useMspEmailSecurityAllowBlockListStore = defineStore(
  StoreModule.MSP_EMAIL_SECURITY_ALLOW_BLOCK_LIST,
  {
    state: (): MspEmailSecurityAllowBlockListState => ({
      ...defaultMspEmailSecurityAllowBlockListState,
    }),
    actions: {
      setPagination(pagination: Pagination): void {
        this.pagination = pagination;
      },
      setSortingOptions(sortingOptions: SortItem[]): void {
        const firstSortOption = sortingOptions[0];
        this.sortingOptions = firstSortOption
          ? `${firstSortOption.key},${firstSortOption.order}`
          : undefined;
      },

      /**
       * Reset pagination to default values
       */
      resetPagination(): void {
        this.pagination = { ...defaultMspEmailSecurityAllowBlockListState.pagination };
      },
      async getAllowBlockList(): Promise<void> {
        this.loading = true;

        const filtersStore = useFiltersStore();
        const filters =
          filtersStore.filters[FilterContext.MSP_EMAIL_SECURITY_ALLOW_BLOCK_LIST_FILTERS];
        const request = {
          ...api.mspEmailSecurityAllowBlockList(),
          params: {
            ...filters,
            ...this.pagination,
            sort: this.sortingOptions,
          },
        };

        try {
          const { data } = await axiosInstance.request<Paged<AllowBlockListItem>>(request);

          this.allowBlockList = data;
        } catch (error) {
          console.error("Failed to fetch allow/block list:", error);
        } finally {
          this.loading = false;
        }
      },

      /**
       * Add items to the allow or block list
       * @param payload - The form data containing items to add
       * @returns Promise that resolves when the operation is complete
       */
      async addToAllowBlockList(payload: AllowBlockListForm): Promise<void> {
        const { items, action } = payload;
        const data = {
          items,
          allowList: action === PhishingAction.ADD_TO_ALLOWLIST,
        };

        const request = {
          ...api.mspEmailSecurityAllowBlockList(),
          data,
          method: "POST",
        };

        try {
          await axiosInstance.request(request);
          await this.getAllowBlockList();
        } catch (error) {
          console.error("Failed to add to allow/block list:", error);
          throw error;
        }
      },

      async validateAllowBlockTerms(
        payload: ValidateAllowBlockTermsPayload
      ): Promise<ValidateAllowBlockTermsResponse> {
        const request = {
          ...api.validateMspEmailSecurityAllowBlockList(),
          data: payload,
        };

        const { data } = await axiosInstance.request<ValidateAllowBlockTermsResponse>(request);

        return data;
      },
      async getExistingTlds(): Promise<string[] | null> {
        try {
          const { data } = await axiosInstance.request<TldTermsResponse>({
            ...api.mspEmailSecurityExistingTlds(),
          });

          return data?.terms ?? [];
        } catch (error) {
          console.error("Failed to fetch existing TLDs:", error);

          return null;
        }
      },
      async getRestrictedTlds(): Promise<string[] | null> {
        try {
          const { data } = await axiosInstance.request<TldTermsResponse>({
            ...api.mspEmailSecurityRestrictedTlds(),
          });

          return data?.terms ?? [];
        } catch (error) {
          console.error("Failed to fetch restricted TLDs:", error);

          return null;
        }
      },
      async removeFromAllowBlockList(payload: AllowBlockListItem) {
        const request = {
          ...api.mspEmailSecurityAllowBlockList(),
          method: "DELETE",
          params: {
            name: payload.name,
            listType: payload.allowList ? AllowBlockListType.ALLOW : AllowBlockListType.BLOCK,
            senderClassificationType: payload.senderClassificationType,
          },
        };

        try {
          await axiosInstance.request(request);

          await this.getAllowBlockList();
        } catch (error) {
          console.error("Failed to remove from allow/block list:", error);
          throw error;
        }
      },

      /**
       * Import allow/block list from a CSV file
       * @param file - The CSV file to import
       * @returns Promise that resolves with the API response
       */
      async importAllowBlockListFromCsv(file: File) {
        const data = new FormData();
        data.append("file", file);
        const request = {
          ...api.mspImportEmailSecurityAllowBlockList(),
          data,
        };

        try {
          return await axiosInstance.request(request);
        } catch (error) {
          console.error("Failed to import allow/block list from CSV:", error);
          throw error;
        }
      },

      /**
       * Download an example CSV file for allow/block list import
       * @returns Promise that resolves when the download is complete
       */
      async downloadAllowBlockListExampleCsv(): Promise<void> {
        const request = {
          ...api.mspEmailSecurityAllowBlockListExampleCSV(),
        };

        try {
          const { data, headers } = await axiosInstance.request(request);
          const blob = new Blob([data], { type: headers["content-type"] as string | undefined });
          const fileName = "EmailSecurityAllowBlockListExample.csv";

          downloadFile(blob, fileName);
        } catch (error) {
          console.error("Failed to download allow/block list example CSV:", error);
        }
      },
    },
  }
);
